import { useState, useEffect, useCallback } from 'react'
import { supabase } from './supabase'

/**
 * useSubServices — fetches sub_services from Supabase.
 * Falls back to [] on error.
 *
 * @param {object|null} session  — Supabase session (used for updated_by)
 * @returns {{ items, loading, error, createSubService, updateSubService, removeSubService, reload }}
 */
export function useSubServices(session) {
    const [items,   setItems]   = useState([])
    const [loading, setLoading] = useState(true)
    const [error,   setError]   = useState(null)
    const userId = session?.user?.id || null

    const load = useCallback(async () => {
        setLoading(true)
        setError(null)
        const { data, error: err } = await supabase
            .from('sub_services')
            .select('*')
            .order('cat',  { ascending: true, nullsFirst: false })
            .order('text', { ascending: true })
        if (err) {
            console.error('[subServices] fetch error:', err.message)
            setError(err.message)
            setItems([])
        } else {
            setItems(data || [])
        }
        setLoading(false)
    }, [])

    useEffect(() => { load() }, [load])

    /**
     * Create a new sub-service.
     * Checks for an existing row with the same trimmed text (case-insensitive)
     * to avoid duplicates. Returns the existing row if found.
     */
    const createSubService = useCallback(async ({ text, cat = null, group_id = null }) => {
        const trimmed = text.trim()
        if (!trimmed) throw new Error('text is required')

        // Duplicate guard — find exact case-insensitive match
        const existing = items.find(
            s => s.text.trim().toLowerCase() === trimmed.toLowerCase() && s.cat === cat
        )
        if (existing) return existing

        const payload = {
            text:       trimmed,
            cat:        cat  || null,
            group_id:   (typeof group_id !== 'undefined' ? group_id : null),
            updated_by: userId,
        }
        const { data, error: err } = await supabase
            .from('sub_services')
            .insert(payload)
            .select()
            .single()
        if (err) {
            console.error('[subServices] insert error:', err.message)
            throw new Error(err.message)
        }
        setItems(prev => [...prev, data].sort((a, b) => {
            const catCmp = (a.cat || '').localeCompare(b.cat || '')
            return catCmp !== 0 ? catCmp : a.text.localeCompare(b.text)
        }))
        return data
    }, [items, userId])

    /** Update a sub-service's text, cat, or group_id. */
    const updateSubService = useCallback(async (id, patch) => {
        const { data, error: err } = await supabase
            .from('sub_services')
            .update({ ...patch, updated_by: userId })
            .eq('id', id)
            .select()
            .single()
        if (err) {
            console.error('[subServices] update error:', err.message)
            throw new Error(err.message)
        }
        setItems(prev => prev.map(s => s.id === id ? data : s))
        return data
    }, [userId])

    /** Delete a sub-service by id. */
    const removeSubService = useCallback(async (id) => {
        setItems(prev => prev.filter(s => s.id !== id))
        const { error: err } = await supabase
            .from('sub_services')
            .delete()
            .eq('id', id)
        if (err) {
            console.error('[subServices] delete error:', err.message)
            throw new Error(err.message)
        }
    }, [])

    /**
     * Update text with cascade: updates sub_services.text in DB, then rebuilds
     * inc/exc text arrays on every catalog_item that references this id, keeping
     * both columns in sync. Also updates local hook state.
     *
     * @param {string}   id             — sub_service UUID to update
     * @param {string}   newText        — new bullet text
     * @param {Array}    allCatalogItems — current catalog items (from useCatalogItems)
     * @param {Function} upsertItem     — catalog upsert fn (from useCatalogItems)
     * @returns {{ updated: object, affectedCount: number }}
     */
    const updateSubServiceText = useCallback(async (id, newText, allCatalogItems, upsertItem) => {
        const result = await updateSubServiceWithCascade(id, newText, allCatalogItems, upsertItem, userId)
        setItems(prev => prev.map(s => s.id === id ? result.updated : s))
        return result
    }, [userId])

    return { items, loading, error, createSubService, updateSubService, updateSubServiceText, removeSubService, reload: load }
}

/** Build a fast id→subService lookup map. */
export function buildSubServiceMap(items) {
    return Object.fromEntries(items.map(s => [s.id, s]))
}

/**
 * Update a sub_service's text in the DB and cascade that change to all
 * catalog_items referencing it via inc_ids / exc_ids.
 *
 * inc[i] corresponds positionally to inc_ids[i] — this invariant is maintained
 * by CatalogEditorTab (handleIdsChange derives inc texts from sub_services).
 *
 * @param {string}      id             — sub_service UUID
 * @param {string}      newText        — new bullet text
 * @param {Array}       allCatalogItems — from useCatalogItems
 * @param {Function}    upsertItem     — from useCatalogItems
 * @param {string|null} userId
 * @returns {{ updated: object, affectedCount: number }}
 */
export async function updateSubServiceWithCascade(id, newText, allCatalogItems, upsertItem, userId) {
    const trimmed = newText.trim()
    if (!trimmed) throw new Error('text is required')

    const { data, error: err } = await supabase
        .from('sub_services')
        .update({ text: trimmed, updated_by: userId })
        .eq('id', id)
        .select()
        .single()
    if (err) throw new Error(err.message)

    const affected = allCatalogItems.filter(item =>
        (item.inc_ids || []).includes(id) || (item.exc_ids || []).includes(id)
    )
    for (const item of affected) {
        const newInc = (item.inc_ids || []).map((sid, i) =>
            sid === id ? trimmed : ((item.inc || [])[i] || '')
        )
        const newExc = (item.exc_ids || []).map((sid, i) =>
            sid === id ? trimmed : ((item.exc || [])[i] || '')
        )
        await upsertItem({ ...item, inc: newInc, exc: newExc })
    }

    return { updated: data, affectedCount: affected.length }
}

/**
 * Compute how many catalog items reference each sub-service.
 * Returns a Map<sub_service_id, { total, inc, exc, items: [{id,name,cat,subcat,inc_ids,exc_ids}] }>
 *
 * @param {Array} subServices  — from useSubServices
 * @param {Array} catalogItems — from useCatalogItems
 */
export function computeUsageCounts(subServices, catalogItems) {
    const map = new Map()
    for (const s of subServices) {
        map.set(s.id, { total: 0, inc: 0, exc: 0, items: [] })
    }
    for (const item of catalogItems) {
        const incIds = item.inc_ids || []
        const excIds = item.exc_ids || []
        const slim   = { id: item.id, name: item.name, cat: item.cat, subcat: item.subcat || null, inc_ids: incIds, exc_ids: excIds }
        for (const id of incIds) {
            if (map.has(id)) {
                const entry = map.get(id)
                entry.inc++
                entry.total++
                entry.items.push({ ...slim, _side: 'inc' })
            }
        }
        for (const id of excIds) {
            if (map.has(id)) {
                const entry = map.get(id)
                entry.exc++
                entry.total++
                entry.items.push({ ...slim, _side: 'exc' })
            }
        }
    }
    return map
}
